import { Box, Button, type SxProps } from '@mui/material'
import { graphHooks } from '@tunasong/graph-lib/react'
import { Controls } from '@tunasong/icons'
import { SystemTags, canWriteEntity, hasSystemTag } from '@tunasong/models'
import type { BaseContentContainerProps } from '@tunasong/plugin-lib'
import { useCurrentUser } from '@tunasong/redux'
import type { Entity, Image, ImageElement, Persisted } from '@tunasong/schemas'
import type { FC } from 'react'
import { useRef, useState } from 'react'
import CloseButton from '../dialog/close-button.js'
import Dialog from '../dialog/dialog.js'
import EntityTitle from '../entity/entity-title.js'
import BacklinksButton from '../graph/backlinks-button.js'
import { useMergeRefs } from '../hooks/merge-refs.js'
import type { ScrollState } from '../hooks/scroll-state.js'
import { useScrollState } from '../hooks/scroll-state.js'
import { BackgroundImage } from '../image/background-image.js'
import { HeaderImage } from '../image/header-image.js'
import ContentContainerSettings from './content-container-settings.js'
import { TemplateHeader } from './template-header.js'
import TrashHeader from './trash-header.js'
import { useDeviceInfo } from '../browser/device.hook.js'

export interface ContentContainerProps extends BaseContentContainerProps {
  className?: string
  slots?: {
    EntityControls?: FC<{ entity?: Persisted<Entity>; onActive?: (active: boolean) => void }>
  }
  /** Show a close button, and call onClose when it's clicked */
  onClose?(): void
  /** Called when there is a change in scroll state */
  onScrollChange?(scrollState: ScrollState): void
}

export const ContentContainer: FC<ContentContainerProps> = props => {
  const {
    entity,
    large = false,
    showBacklinks = true,
    showTitle = true,
    onClose,
    slots: { EntityControls } = {},
    children,
    sx,
    contentBoxProps,
    onScrollChange,
    ...restProps
  } = props

  const { isMobile } = useDeviceInfo()
  const sxHoverProps: SxProps = {
    opacity: isMobile ? 1 : 0.5,
    '&:hover': {
      opacity: 1,
    },
  }
  const { scrollRef } = useScrollState({ onScrollChange })

  const { child: backgroundImage } = graphHooks.useEntityChild<Persisted<ImageElement>>({
    parentId: entity?.id,
    filter: hasSystemTag(SystemTags.BackgroundImage),
  })

  const { child: headerImage } = graphHooks.useEntityChild<Persisted<Image>>({
    parentId: entity?.id,
    filter: hasSystemTag(SystemTags.HeaderImage),
  })

  const { userId } = useCurrentUser()
  const canEdit = canWriteEntity(userId, entity)

  const contentRef = useRef<HTMLElement | null>(null)

  const boxRef = useMergeRefs(scrollRef, contentRef)

  // Filter by tags
  const [showContentSettings, setShowContentSettings] = useState(false)

  return (
    // This is the scroll container for the contentEl
    <Box
      {...restProps}
      sx={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        overflow: 'auto',
        height: '100%',
        width: '100%',
        minWidth: 'min(1024px, 100%)',
        maxWidth: large ? 'fit-content' : 1024,
        containerName: 'ContentContainer',

        marginLeft: {
          xs: 0,
          sm: 'auto',
        },
        marginRight: {
          xs: 0,
          sm: 'auto',
        },

        ...sx,
      }}
    >
      <TrashHeader sx={{ flexDirection: 'row' }} entity={entity} />
      <TemplateHeader entity={entity} />
      {headerImage && <HeaderImage image={headerImage} />}
      {backgroundImage ? <BackgroundImage image={backgroundImage} /> : null}
      {entity ? (
        <>
          {/* Header controls & info */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 3fr  1fr',
              justifyContent: 'space-between',
              alignItems: 'center',
              // hack to avoid opacity flickering
              transform: 'rotate(0)',

              transition: theme =>
                theme.transitions.create('opacity', {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.leavingScreen,
                }),
            }}
          >
            <Box
              sx={{
                justifySelf: 'left',
                ...sxHoverProps,
              }}
            >
              <Button startIcon={<Controls />} tabIndex={-1} onClick={() => setShowContentSettings(true)} />
            </Box>
            {showTitle ? <EntityTitle entity={entity} disabled={!canEdit} /> : <Box />}

            {/* Right */}
            <Box
              sx={{
                justifySelf: 'right',
                ...sxHoverProps,
              }}
            >
              {showBacklinks ? <BacklinksButton tabIndex={-1} id={entity.id} /> : null}
              {onClose ? <CloseButton onClose={onClose} /> : null}
            </Box>
          </Box>
        </>
      ) : null}
      <Box
        {...contentBoxProps}
        ref={boxRef}
        sx={{
          flex: 1,
          flexDirection: 'column',
          display: 'flex',
          width: '100%',
          margin: {
            xs: 0,
            sm: 'auto',
          },
          padding: theme => ({
            xs: theme.spacing(0, 1, 0, 1),
            sm: theme.spacing(0, 4, 0, 4),
          }),

          transition: theme =>
            theme.transitions.create('margin', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
          ...contentBoxProps?.sx,
        }}
      >
        {entity ? (
          <Dialog
            title="Settings"
            maxWidth="xs"
            fullWidth
            open={showContentSettings}
            onClose={() => setShowContentSettings(false)}
          >
            <ContentContainerSettings entity={entity} EntityControls={EntityControls} />
          </Dialog>
        ) : null}
        {children}
      </Box>
    </Box>
  )
}

export default ContentContainer
